import { Shield, UserPlus, RefreshCw, Lock, Unlock } from 'lucide-react';
import { AdminUser } from '../../../api/admin';
import { cn } from '../../../utils/responsive';
import { formatThaiDate } from '../../../utils/dateUtils';
import { ROLE_LABELS, STATUS_LABELS } from '../constants';

interface AdminUserSectionProps {
    users: AdminUser[];
    total: number;
    isLoading: boolean;
    isMobileOrTablet: boolean;
    onAddUser: () => void;
    onToggleStatus: (user: AdminUser) => void;
    onEdit: (user: AdminUser) => void;
    onDelete: (user: AdminUser) => void;
}

export function AdminUserSection({
    users,
    total,
    isLoading,
    isMobileOrTablet,
    onAddUser,
    onToggleStatus,
    onEdit,
    onDelete,
}: AdminUserSectionProps) {
    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-stone-200 dark:border-gray-700 overflow-hidden">
            <div className={cn(
                "flex items-center border-b border-stone-200 dark:border-gray-700 px-4 lg:px-6 py-3 lg:py-4",
                isMobileOrTablet ? "flex-col gap-3" : "justify-between"
            )}>
                <h2 className={cn(
                    "font-semibold text-stone-900 dark:text-gray-100 flex items-center gap-2",
                    isMobileOrTablet ? "text-base w-full" : "text-lg"
                )}>
                    <Shield className={cn(
                        "text-yellow-600",
                        isMobileOrTablet ? "w-4 h-4" : "w-5 h-5"
                    )} />
                    พนักงาน ({users.length}/{total})
                </h2>
                <button
                    onClick={onAddUser}
                    className={cn(
                        "inline-flex items-center justify-center gap-2",
                        "rounded-lg bg-gradient-to-r from-yellow-400 to-orange-500",
                        "px-4 py-2 text-sm font-semibold text-white",
                        "shadow-sm hover:shadow-accent hover:scale-[1.02]",
                        "transition-all duration-200 touch-target",
                        isMobileOrTablet && "w-full"
                    )}
                >
                    <UserPlus className="w-4 h-4" />
                    เพิ่มพนักงาน
                </button>
            </div>
            {isLoading ? (
                <div className="flex items-center justify-center py-12 text-stone-500 dark:text-gray-400">
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                    กำลังโหลดข้อมูล...
                </div>
            ) : users.length === 0 ? (
                <div className="py-10 text-center text-sm text-stone-500 dark:text-gray-400">ไม่พบพนักงานที่ตรงกับเงื่อนไข</div>
            ) : (
                <ul className="divide-y divide-stone-200 dark:divide-gray-700">
                    {users.map(user => (
                        <li key={user.id} className={cn(
                            "space-y-3",
                            isMobileOrTablet ? "p-4" : "p-6"
                        )}>
                            <div className={cn(
                                "flex items-start gap-3",
                                isMobileOrTablet ? "flex-col" : "justify-between"
                            )}>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-stone-500 dark:text-gray-400 font-mono">ID: {user.id}</p>
                                    <p className={cn(
                                        "font-semibold text-stone-900 dark:text-gray-100 mt-1",
                                        isMobileOrTablet ? "text-base" : "text-lg"
                                    )}>{user.username}</p>
                                    <p className="text-xs text-stone-600 dark:text-gray-400">บทบาท: {ROLE_LABELS[user.role] || user.role}</p>
                                    <p className="text-xs text-stone-400 dark:text-gray-500 font-mono mt-1">
                                        {formatThaiDate(user.createdAt)}
                                    </p>
                                </div>
                                <div className={cn(
                                    "flex items-center gap-2",
                                    isMobileOrTablet ? "flex-col w-full" : "flex-wrap"
                                )}>
                                    <button
                                        onClick={() => onToggleStatus(user)}
                                        className={cn(
                                            "inline-flex items-center justify-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium",
                                            "transition-all duration-200 touch-target",
                                            user.status === 'active'
                                                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/40'
                                                : 'bg-stone-100 dark:bg-gray-700 text-stone-600 dark:text-gray-300 hover:bg-stone-200 dark:hover:bg-gray-600',
                                            isMobileOrTablet && "w-full"
                                        )}
                                    >
                                        {user.status === 'active' ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                                        {STATUS_LABELS[user.status] || user.status}
                                    </button>
                                    <button
                                        onClick={() => onEdit(user)}
                                        className={cn(
                                            "text-xs font-medium text-blue-600 hover:underline touch-target",
                                            isMobileOrTablet && "w-full text-center py-2"
                                        )}
                                    >
                                        แก้ไข
                                    </button>
                                    {!user.isDefault && (
                                        <button
                                            onClick={() => onDelete(user)}
                                            className={cn(
                                                "text-xs font-medium text-red-600 hover:underline touch-target",
                                                isMobileOrTablet && "w-full text-center py-2"
                                            )}
                                        >
                                            ลบผู้ใช้
                                        </button>
                                    )}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
